import { useMemo } from 'react';
import { useChartsTheme } from '@/components';
import { TimeSeriesOption } from '@/core';

interface UseScrollMarkerProps {
  timeSeriesMapping: TimeSeriesOption[];
  scrollMarkerTimestampMs?: number | null;
}

const SCROLL_MARKER_LINE_WIDTH = 1;
const SCROLL_MARKER_LINE_OPACITY = 0.9;
const SCROLL_MARKER_STRIP_WIDTH = 3;
const SCROLL_MARKER_STRIP_OPACITY = 0.5;

/**
 * Appends a synthetic ECharts series that renders a dashed vertical marker line
 * at the given timestamp. The marker is non-interactive and does not affect
 * tooltips or legend.
 */
export function useScrollMarker({ timeSeriesMapping, scrollMarkerTimestampMs }: UseScrollMarkerProps) {
  const chartsTheme = useChartsTheme();
  const scrollMarkerColor = chartsTheme.theme.featureFlags.panwRebrandStubYear
    ? chartsTheme.theme.palette.border.bold
    : chartsTheme.theme.palette.grey[500];

  const seriesMapping = useMemo(() => {
    if (scrollMarkerTimestampMs == null) return timeSeriesMapping;

    const scrollMarkerLineStyle = {
      type: 'dashed' as const,
      width: SCROLL_MARKER_LINE_WIDTH,
      color: scrollMarkerColor,
      opacity: SCROLL_MARKER_LINE_OPACITY,
    };

    const scrollMarkerSeries = {
      name: '__scroll_marker__',
      type: 'line' as const,
      data: [],
      lineStyle: { width: 0 },
      symbol: 'none',
      zlevel: 1,
      markLine: {
        symbol: 'none',
        label: { show: false },
        data: [
          {
            xAxis: scrollMarkerTimestampMs,
            lineStyle: {
              type: 'solid' as const,
              width: SCROLL_MARKER_STRIP_WIDTH,
              color: '#ffffff',
              opacity: SCROLL_MARKER_STRIP_OPACITY,
            },
          },
          { xAxis: scrollMarkerTimestampMs },
          [
            { xAxis: scrollMarkerTimestampMs, y: 0 },
            { xAxis: scrollMarkerTimestampMs, y: 30 },
          ],
        ],
        lineStyle: scrollMarkerLineStyle,
        emphasis: { lineStyle: scrollMarkerLineStyle },
        blur: { lineStyle: scrollMarkerLineStyle },
        silent: true,
        animation: false,
      },
      tooltip: { trigger: 'none' as const },
      silent: true,
    } as TimeSeriesOption;

    return [...timeSeriesMapping, scrollMarkerSeries];
  }, [timeSeriesMapping, scrollMarkerTimestampMs, scrollMarkerColor]);

  return { seriesMapping };
}
